import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/cjs/styles/prism';

type Props = {
    value: string;
    onChange: (value: string) => void;
    readOnly?: boolean;
};

const JsonPayloadEditor = ({ value, onChange, readOnly = false }: Props) => {
    const { t } = useTranslation();
    const [showPreview, setShowPreview] = useState(false);

    const validationError = useMemo(() => {
        if (!value.trim()) return null;
        try {
            JSON.parse(value);
            return null;
        } catch (error) {
            return error instanceof Error ? error.message : t('integrationMessages.payload.invalid');
        }
    }, [value, t]);

    const handleFormat = () => {
        try {
            const formatted = JSON.stringify(JSON.parse(value), null, 2);
            onChange(formatted);
        } catch {
            // keep invalid value; validation message is shown below
        }
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" onClick={handleFormat} disabled={readOnly}>
                    {t('integrationMessages.payload.format')}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowPreview((current) => !current)}
                >
                    {showPreview
                        ? t('integrationMessages.payload.edit')
                        : t('integrationMessages.payload.preview')}
                </Button>
            </div>

            {showPreview && !validationError ? (
                <div className="overflow-auto rounded-lg border border-lightgray">
                    <SyntaxHighlighter language="json" style={dracula} customStyle={{ margin: 0, minHeight: 240 }}>
                        {value}
                    </SyntaxHighlighter>
                </div>
            ) : (
                <textarea
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    readOnly={readOnly}
                    rows={16}
                    className="w-full rounded-lg border border-lightgray bg-base px-3 py-2 font-mono text-sm text-black"
                    spellCheck={false}
                />
            )}

            {validationError && (
                <p className="text-sm text-destructive">{validationError}</p>
            )}
        </div>
    );
};

export default JsonPayloadEditor;
